/**
 * Pure helpers for the Lead Sources page — vocabulary, tones and the one
 * sentence per blocked list that the owner must be able to read at a glance.
 */
import type { PillTone } from "../components/Pill";
import type {
  BlockedReason,
  CandidateFit,
  HandoffStage,
  LeadCandidate,
  LeadSource,
} from "../api/leadSources";

export type LeadView = "leads" | "sources" | "register";

export const LEAD_FILTERS = [
  { value: "proposed", label: "Awaiting you" },
  { value: "interesting", label: "Interesting" },
  { value: "not_interesting", label: "Not interesting" },
  { value: "all", label: "All" },
] as const;

export type LeadFilter = (typeof LEAD_FILTERS)[number]["value"];

const BLOCKED_SENTENCE: Record<BlockedReason, string> = {
  robots: "robots.txt disallows crawling – manual review",
  terms: "terms forbid automated access – manual review",
  login: "list sits behind a login – manual review",
};

export function blockedSentence(reason: BlockedReason | null): string {
  return reason ? BLOCKED_SENTENCE[reason] : "blocked – manual review";
}

/** Status pill for a source: blocked and overdue are the states that need eyes. */
export function sourceStatusPill(source: LeadSource): { label: string; tone: PillTone } {
  if (source.status === "blocked") {
    return { label: `blocked · ${source.blocked_reason ?? "?"}`, tone: "alert" };
  }
  if (source.status === "active" && source.overdue) return { label: "overdue", tone: "signal" };
  if (source.status === "active") return { label: "active", tone: "ok" };
  return { label: source.status, tone: "muted" };
}

/** Where the owner's manual review of a blocked list stands. */
export function reviewTaskLabel(source: LeadSource): string {
  const task = source.manual_review_task;
  if (!source.manual_review_task_id || !task) return "review task not filed yet";
  if (task.status === "completed") {
    return `reviewed ${task.completed_at ? task.completed_at.slice(0, 10) : ""}`.trim();
  }
  if (task.status === "dismissed") return "review declined";
  return "review task open";
}

export function fitTone(fit: CandidateFit): PillTone {
  return fit === "high" ? "ok" : fit === "medium" ? "signal" : "muted";
}

const STAGE_LABEL: Record<HandoffStage, { label: string; tone: PillTone }> = {
  queued: { label: "queued for Seth", tone: "signal" },
  with_seth: { label: "with Seth", tone: "signal" },
  done: { label: "contacts created", tone: "ok" },
  failed: { label: "Seth failed", tone: "alert" },
  canceled: { label: "hand-off canceled", tone: "muted" },
};

export function handoffPill(stage: HandoffStage | null): { label: string; tone: PillTone } | null {
  return stage ? STAGE_LABEL[stage] : null;
}

/** A decision can be taken back while Seth hasn't picked the lead up. */
export function canUndo(candidate: LeadCandidate): boolean {
  if (candidate.status === "not_interesting") return true;
  return candidate.status === "interesting" && candidate.handoff_stage === "queued";
}

/** Seconds until Seth may claim the hand-off (0 once the window passed). */
export function secondsUntil(iso: string | null | undefined, now: Date = new Date()): number {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? 0 : Math.max(0, Math.round((t - now.getTime()) / 1000));
}
